import type {
  AggregationType,
  BaseAggregationType,
  CityConfigId,
  CompanyId,
  CompanyPdrn,
  CountryCodeAlpha3,
  CourierGroupId,
  CourierId,
  CourierMetricsV1Dimension,
  CourierPdrn,
  ScheduleInfoDateRange,
  SchedulePlanId,
  UnifiedCourierId,
  VehicleType,
  WoltUserId,
} from 'common/api/api-types';
import type { ScorecardAggType } from 'common/api/scorecard';
import type { TravelledDistanceVersion } from 'common/api/travelledDistanceEndpoint';

import { sort } from 'd3';
import { getUnixTime } from 'date-fns';

export default {
  forUseBulkSchedule({
    companyId,
    schedulePlanId,
    localStartDate,
    localEndDate,
  }: {
    companyId?: CompanyId;
    schedulePlanId?: SchedulePlanId;
    localStartDate?: ScheduleInfoDateRange['startDate'];
    localEndDate?: ScheduleInfoDateRange['endDate'];
  }) {
    return [
      'GET',
      'companies',
      companyId ?? '<none>',
      'schedules',
      'bulk-view',
      `${localStartDate ?? '<none>'}-${localEndDate ?? '<none>'}-${schedulePlanId ?? '<none>'}`,
    ];
  },
  forUseCouriersByCompanyId({ version, id }: { version: 'v2' | 'v3'; id?: string }) {
    return ['GET', version, 'companies', id ?? '<none>', 'couriers'];
  },
  forUseStatusesByCompanyId({ companyId }: { companyId?: CompanyId }) {
    return ['GET', 'companies', companyId ?? '<none>', 'statuses'];
  },
  forUseStatusesByCompanyPdrn({ companyPdrn }: { companyPdrn?: CompanyPdrn }) {
    return ['GET', 'v2', 'companies', companyPdrn ?? '<none>', 'statuses'];
  },
  forUseDeliveryStatusesByCompanyId({ companyId }: { companyId?: CompanyId }) {
    return ['GET', 'companies', companyId ?? '<none>', 'deliveryStatuses'];
  },
  forUseCourierLocationsByCompanyId({ companyId }: { companyId?: CompanyId }) {
    return ['GET', 'companies', companyId ?? '<none>', 'locations'];
  },
  forUseVenuesByCompanyId({ companyId }: { companyId?: CompanyId }) {
    return ['GET', 'v2', 'companies', companyId ?? '<none>', 'venues'];
  },
  forUseCompanyContractTypes({ companyId }: { companyId?: CompanyId }) {
    return ['GET', 'company', companyId ?? '<none>', 'contractTypes'];
  },
  forUseCourier({
    version = 'v2',
    companyId,
    companyPdrn,
    courierId,
    courierPdrn,
  }: {
    version?: 'v2' | 'v3';
    companyId?: CompanyId;
    companyPdrn?: CompanyPdrn;
    courierId?: UnifiedCourierId;
    courierPdrn?: CourierPdrn;
  }) {
    const companyKey = version === 'v3' ? (companyPdrn ?? '<none>') : (companyId ?? '<none>');
    const courierKey = version === 'v3' ? (courierPdrn ?? '<none>') : (courierId ?? '<none>');
    return ['GET', version, 'companies', companyKey, 'couriers', courierKey];
  },
  forUseCourierStatus({
    companyId,
    courierId,
  }: {
    companyId?: CompanyId;
    courierId?: UnifiedCourierId;
  }) {
    return ['GET', 'companies', companyId ?? '<none>', 'couriers', courierId ?? '<none>', 'status'];
  },
  forUseCourierDeliveryStatus({
    companyId,
    courierId,
  }: {
    companyId?: CompanyId;
    courierId?: UnifiedCourierId;
  }) {
    return [
      'GET',
      'companies',
      companyId ?? '<none>',
      'couriers',
      courierId ?? '<none>',
      'deliveryStatus',
    ];
  },
  forUseCourierLocation({
    companyId,
    courierId,
  }: {
    companyId?: CompanyId;
    courierId?: UnifiedCourierId;
  }) {
    return [
      'GET',
      'companies',
      companyId ?? '<none>',
      'couriers',
      courierId ?? '<none>',
      'location',
    ];
  },
  forUseCourierLocationHistory({
    companyId,
    courierId,
    from,
    to,
  }: {
    companyId?: CompanyId;
    courierId?: UnifiedCourierId;
    from: Date;
    to: Date;
  }) {
    return [
      'GET',
      'companies',
      companyId ?? '<none>',
      'couriers',
      courierId ?? '<none>',
      'location-history',
      `${getUnixTime(from)}-${getUnixTime(to)}`,
    ];
  },
  forUseCourierMetricsByCompanyId({
    aggregationType,
    companyId,
    from,
    to,
  }: {
    aggregationType?: AggregationType;
    companyId?: CompanyId;
    from: Date;
    to: Date;
  }) {
    return [
      'GET',
      'companies',
      companyId ?? '<none>',
      'metrics',
      'v4',
      `${getUnixTime(from)}-${getUnixTime(to)}`,
      `aggregationType=${aggregationType ?? '<none>'}`,
    ];
  },
  forUseCourierMetricsV1ByCompanyPdrn({
    companyPdrn,
    aggregationType,
    from,
    to,
    courierPdrns,
    dimension,
  }: {
    companyPdrn?: CompanyPdrn;
    aggregationType: BaseAggregationType;
    from?: Date;
    to?: Date;
    courierPdrns?: CourierPdrn[];
    dimension?: CourierMetricsV1Dimension;
  }) {
    return [
      'GET',
      'v1',
      'companies',
      companyPdrn ?? '<none>',
      'courier-metrics',
      `${from ? getUnixTime(from) : '<none>'}-${to ? getUnixTime(to) : '<none>'}`,
      `aggregationType=${aggregationType}`,
      `courierPdrns=${courierPdrns?.join(',') ?? '<none>'}`,
      `dimension=${dimension ?? '<none>'}`,
    ];
  },
  forUseCourierCashBalancesByCompanyId({ companyId }: { companyId?: CompanyId }) {
    return ['GET', 'companies', companyId ?? '<none>', 'cash-balances'];
  },
  forUseCourierMetrics({
    aggregationType,
    companyId,
    courierId,
    from,
    to,
  }: {
    aggregationType?: AggregationType;
    companyId?: CompanyId;
    courierId?: UnifiedCourierId;
    from?: Date;
    to?: Date;
  }) {
    return [
      'GET',
      'companies',
      companyId ?? '<none>',
      'couriers',
      courierId ?? '<none>',
      'metrics',
      'v4',
      `${from ? getUnixTime(from) : '<none>'}-${to ? getUnixTime(to) : '<none>'}`,
      `aggregationType=${aggregationType ?? '<none>'}`,
    ];
  },
  forUseCourierCashBalance({
    companyId,
    courierId,
  }: {
    companyId?: CompanyId;
    courierId?: UnifiedCourierId;
  }) {
    return [
      'GET',
      'companies',
      companyId ?? '<none>',
      'couriers',
      courierId ?? '<none>',
      'cash-balance',
    ];
  },
  forUseAreasByCompanyId({ companyId }: { companyId?: CompanyId }) {
    return ['GET', 'companies', companyId ?? '<none>', 'areas'];
  },

  forUseCourierGroupsByCompanyId({ companyId }: { companyId?: CompanyId }) {
    return ['GET', 'companies', companyId ?? '<none>', 'groups'];
  },

  forUseCourierGroupsByCompanyPdrn({ companyPdrn }: { companyPdrn?: CompanyPdrn }) {
    return ['GET', 'v2', 'companies', companyPdrn ?? '<none>', 'groups'];
  },

  forUseCourierGroupsById({ companyId, id }: { companyId?: CompanyId; id?: CourierGroupId }) {
    return ['GET', 'companies', companyId ?? '<none>', 'groups', id ?? '<none>'];
  },

  forUseCourierEarningsByCompanyId({
    companyId,
    from,
    to,
  }: {
    companyId?: CompanyId;
    from: Date;
    to: Date;
  }) {
    return [
      'GET',
      'companies',
      companyId ?? '<none>',
      'earnings',
      `${getUnixTime(from)}-${getUnixTime(to)}`,
    ];
  },

  forUseCityConfigByCountryCity({ countryCode }: { countryCode?: string }) {
    return ['GET', 'cityConfig', countryCode ?? '<none>'];
  },

  forUseCourierTravelledDistanceByCompanyId({
    companyId,
    from,
    to,
    version,
  }: {
    companyId?: CompanyId;
    from: Date;
    to: Date;
    version: TravelledDistanceVersion;
  }) {
    return [
      'GET',
      'companies',
      companyId ?? '<none>',
      'distance',
      version,
      `${getUnixTime(from)}-${getUnixTime(to)}`,
    ];
  },

  forUseCourierTravelledDistance({
    companyId,
    courierId,
    from,
    to,
    version,
  }: {
    companyId?: CompanyId;
    courierId?: UnifiedCourierId;
    from: Date;
    to: Date;
    version: TravelledDistanceVersion;
  }) {
    return [
      'GET',
      'companies',
      companyId ?? '<none>',
      'couriers',
      courierId ?? '<none>',
      'distance',
      version,
      `${getUnixTime(from)}-${getUnixTime(to)}`,
    ];
  },

  forUseCourierDeliveries({
    companyId,
    courierId,
    from,
    to,
  }: {
    companyId?: CompanyId;
    courierId?: UnifiedCourierId;
    from: Date;
    to: Date;
  }) {
    return [
      'GET',
      'companies',
      companyId ?? '<none>',
      'couriers',
      courierId ?? '<none>',
      'deliveries',
      `${getUnixTime(from)}-${getUnixTime(to)}`,
    ];
  },
  forUseExperiments({
    names,
    courierId,
    city,
    countryCode,
    vehicleType,
  }: {
    names: string[];
    courierId?: CourierId;
    city?: CityConfigId;
    countryCode?: CountryCodeAlpha3;
    vehicleType?: VehicleType;
  }) {
    return [
      'POST',
      'experiments',
      {
        names: sort(names),
        courierId: courierId ?? 'N/A',
        city: city ?? 'N/A',
        countryCode: countryCode ?? 'N/A',
        vehicleType: vehicleType ?? 'N/A',
      },
    ];
  },
  forUseFeatureFlags({
    courierId,
    city,
    countryCode,
    vehicleType,
  }: {
    courierId?: CourierId;
    city?: CityConfigId;
    countryCode?: CountryCodeAlpha3;
    vehicleType?: VehicleType;
  }) {
    return [
      'POST',
      'feature-flags',
      {
        courierId: courierId ?? 'N/A',
        city: city ?? 'N/A',
        countryCode: countryCode ?? 'N/A',
        vehicleType: vehicleType ?? 'N/A',
      },
    ];
  },
  forUseConfigurations({
    companyId,
    countryCode,
    slug,
  }: {
    companyId?: CompanyId;
    countryCode?: CountryCodeAlpha3;
    slug: string;
  }) {
    return [
      'GET',
      'configurations',
      slug,
      {
        companyId: companyId ?? 'N/A',
        countryCode: countryCode ?? 'N/A',
      },
    ];
  },
  forCompanySettings({ companyPdrn }: { companyPdrn?: CompanyPdrn }) {
    return ['GET', 'companies', companyPdrn ?? '<none>', 'settings'];
  },
  forUseCourierOnboardings({ companyId }: { companyId?: CompanyId }) {
    return ['GET', 'companies', companyId ?? '<none>', 'courier-onboardings'];
  },
  forUseCourierOnboardingsV2({ companyId }: { companyId?: CompanyId }) {
    return ['GET', 'v2', 'companies', companyId ?? '<none>', 'courier-onboardings'];
  },
  forUseCourierOnboardingsV3({ companyPdrn }: { companyPdrn?: CompanyPdrn }) {
    return ['GET', 'v3', 'companies', companyPdrn ?? '<none>', 'courier-onboardings'];
  },
  forUseCodEnrollmentsByCompanyId({ companyId }: { companyId?: CompanyId }) {
    return ['GET', 'companies', companyId ?? '<none>', 'cod-enrollments'];
  },
  forUseDeliveryOSSurvey({ userId, slug }: { userId?: WoltUserId; slug: string }) {
    return [
      'GET',
      'feedback',
      'users',
      userId,
      {
        slug,
      },
    ];
  },
  forUseEdgeCampingIncidentsByCompanyId({
    companyId,
    from,
    to,
  }: {
    companyId?: CompanyId;
    from: Date;
    to: Date;
  }) {
    return [
      'GET',
      'companies',
      companyId ?? '<none>',
      'edge-camping-incidents',
      `${getUnixTime(from)}-${getUnixTime(to)}`,
    ];
  },
  forUseCourierCompany({ courierId }: { courierId?: UnifiedCourierId }) {
    return ['GET', 'couriers', courierId ?? '<none>', 'company'];
  },
  forUseCapabilitiesSettings({ companyId }: { companyId?: CompanyId }) {
    return ['GET', 'companies', companyId ?? '<none>', 'capabilities'];
  },

  forUseSchedulePlans({ companyId }: { companyId?: CompanyId }) {
    return ['GET', 'schedule-plans', 'companies', companyId ?? '<none>', 'plans'];
  },

  forUseCourierShifts({
    companyId,
    courierId,
    from,
    to,
  }: {
    companyId?: CompanyId;
    courierId?: UnifiedCourierId;
    from?: number;
    to?: number;
  }) {
    return [
      'GET',
      'companies',
      companyId ?? '<none>',
      'couriers',
      courierId ?? '<none>',
      'shifts',
      `${from}-${to}`,
    ];
  },

  forUseCourierGroupedTimeslots({
    courierId,
    from,
    to,
    includeInactive,
  }: {
    courierId?: UnifiedCourierId;
    from?: string;
    to?: string;
    includeInactive?: boolean;
  }) {
    return [
      'GET',
      'couriers',
      courierId ?? '<none>',
      'timeslots',
      'grouped',
      `${from ?? '<none>'}-${to ?? '<none>'}${includeInactive ? '?includeInactive=true' : ''}`,
    ];
  },

  forUseCourierScheduleStatistics({
    companyId,
    schedulePlanId,
    from,
    to,
  }: {
    companyId?: CompanyId;
    from?: Date;
    to?: Date;
    schedulePlanId: SchedulePlanId;
  }) {
    return [
      'GET',
      'companies',
      companyId ?? '<none>',
      'schedule-plans',
      schedulePlanId ?? '<none>',
      'stats',
      `${from ? getUnixTime(from) : '<none>'}-${to ? getUnixTime(to) : '<none>'}`,
    ];
  },

  forUseSchedulePlan({
    companyId,
    schedulePlanId,
  }: {
    companyId?: CompanyId;
    schedulePlanId?: SchedulePlanId;
  }) {
    return [
      'GET',
      'schedule-plan',
      'companies',
      companyId ?? '<none>',
      'plans',
      schedulePlanId ?? '<none>',
    ];
  },
  forUseUserInfo() {
    return ['GET', 'user'];
  },
  forUseCompaniesByUserId({ userId }: { userId?: WoltUserId }) {
    return ['GET', 'users', userId ?? '<none>', 'companies'];
  },
  forUseCompanyByCompanyId({ companyId }: { companyId?: CompanyId }) {
    return ['GET', 'companies', companyId ?? '<none>'];
  },
  forUseStartingPointsByCompanyId({ companyId }: { companyId?: CompanyId }) {
    return ['GET', 'companies', companyId ?? '<none>', 'startingPoints'];
  },
  forUseScorecard({
    companyId,
    startDate,
    aggType,
  }: {
    companyId?: CompanyId;
    startDate?: string;
    aggType?: ScorecardAggType;
  }) {
    return [
      'GET',
      'companies',
      companyId ?? '<none>',
      'scorecard',
      startDate ?? '<none>',
      aggType ?? '<none>',
    ];
  },
  forUseUserPermissions({ companyId }: { companyId?: CompanyId }) {
    return ['GET', 'access-control', 'permissions', 'companies', companyId ?? '<none>', 'user'];
  },
  forUseCompanyExternalUsers({ companyId }: { companyId?: CompanyId }) {
    return ['GET', 'companies', companyId ?? '<none>', 'external-users'];
  },
  forUseAllRoles() {
    return ['GET', 'access-control', 'roles'];
  },

  forUseScheduleDashboardByCompany({ companyId }: { companyId?: CompanyId }) {
    return ['GET', 'companies', companyId ?? '<none>', 'schedules', 'dashboard'];
  },

  forUseCityHealth({ companyId }: { companyId?: CompanyId }) {
    return ['GET', 'companies', companyId ?? '<none>', 'city-health'];
  },
};
